// src/components/FileUpload/FileUpload.tsx
import {axiosWithAuth} from 'helper/axiosWithAuth'
import {useState} from 'react'
import swal from 'sweetalert'
import UploadValidationModal, {InvalidRow, ValidationData} from './UploadValidationModal'

interface FileUploadProps {
  uploadUrl: string
  disabled?: boolean
  onSuccess?: () => void
  /** 사용자가 선택한 업로드 유형 ('before' | 'info') */
  uploadType?: 'before' | 'info'
}

type UploadDryRunResponse = {
  success: boolean
  code?: 'MISSING_COLUMNS'
  mode?: 'strict' | 'partial'
  dryRun?: boolean
  totalRows?: number
  validRows?: number
  toUpdate?: number
  skippedKeyOnly?: number
  skippedNoKey?: number
  skippedBlankField?: number
  skippedDuplicate?: number
  missingInDb?: number
  dbDuplicateCount?: number
  invalidRows?: InvalidRow[]
  missingKeys?: string[]
  dbDuplicateKeys?: string[]
  // 헤더 검증
  missingColumns?: string[]
  expectedColumns?: string[]
  receivedColumns?: string[]
}

type UploadFinalResponse = {
  success: boolean
  mode?: 'strict' | 'partial'
  totalRows?: number
  validRows?: number
  updated?: number
  skippedKeyOnly?: number
  skippedNoKey?: number
  skippedBlankField?: number
  skippedDuplicate?: number
  missingInDb?: number
  dbDuplicateCount?: number
}

const FileUpload = ({
  uploadUrl,
  disabled = false,
  onSuccess,
  uploadType = 'info',
}: FileUploadProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)

  // 검증 모달 상태
  const [modalOpen, setModalOpen] = useState(false)
  const [validation, setValidation] = useState<ValidationData | null>(null)

  const isBefore = uploadType === 'before'

  const appendParam = (url: string, key: string, value: string) =>
    url.includes('?')
      ? `${url}&${key}=${encodeURIComponent(value)}`
      : `${url}?${key}=${encodeURIComponent(value)}`

  const handleUpload = async () => {
    if (!selectedFile) {
      await swal({title: '안내', text: '업로드할 파일을 선택하세요.', icon: 'warning', timer: 2000})
      return
    }

    const formData = new FormData()
    formData.append('file', selectedFile)

    try {
      setLoading(true)

      let canProceedToFinal = false
      let dryData: UploadDryRunResponse | null = null

      // ✅ 1) before가 아닐 때만 dryRun 실행
      if (!isBefore) {
        const dryUrl = appendParam(uploadUrl, 'dryRun', '1')
        const dryRes = await axiosWithAuth().post<UploadDryRunResponse>(dryUrl, formData, {
          headers: {'Content-Type': 'multipart/form-data'},
          validateStatus: () => true,
        })

        if (!dryRes || typeof dryRes.status === 'undefined') {
          throw new Error('서버 응답이 없습니다.')
        }

        dryData = dryRes.data

        // 기존 dryRun 검증 로직 유지
        const hasMissingColumns =
          Array.isArray(dryData?.missingColumns) && dryData.missingColumns.length > 0

        if (hasMissingColumns || dryData?.code === 'MISSING_COLUMNS') {
          await swal({
            title: '필수 컬럼 누락',
            text: `누락 컬럼: ${dryData?.missingColumns?.join(', ') || '-'}`,
            icon: 'error',
          })
          return
        }

        const hasInvalid =
          (dryData?.invalidRows?.length ?? 0) > 0 ||
          (dryData?.missingInDb ?? 0) > 0 ||
          (dryData?.dbDuplicateCount ?? 0) > 0

        if (hasInvalid) {
          setValidation({
            totalRows: dryData?.totalRows ?? 0,
            validRows: dryData?.validRows ?? 0,
            skippedKeyOnly: dryData?.skippedKeyOnly,
            skippedNoKey: dryData?.skippedNoKey,
            skippedBlankField: dryData?.skippedBlankField,
            skippedDuplicate: dryData?.skippedDuplicate,
            missingInDb: dryData?.missingInDb,
            dbDuplicateCount: dryData?.dbDuplicateCount,
            invalidRows: dryData?.invalidRows ?? [],
            missingKeys: dryData?.missingKeys ?? [],
            dbDuplicateKeys: dryData?.dbDuplicateKeys ?? [],
          })
          setModalOpen(true)
          return
        }

        canProceedToFinal = true
      } else {
        canProceedToFinal = true
      }

      if (canProceedToFinal) {
        await axiosWithAuth().post(uploadUrl, formData, {
          headers: {'Content-Type': 'multipart/form-data'},
        })

        await swal({
          title: '저장 완료',
          text: isBefore
            ? '전월 자료가 정상적으로 업로드되었습니다.'
            : '파일이 정상적으로 업로드되었습니다.',
          icon: 'success',
          timer: 2200,
        })

        setSelectedFile(null)
        onSuccess?.()
      }
    } catch (error: any) {
      console.error('업로드 실패:', error)
      await swal({
        title: '실패',
        text: error?.response?.data?.message ?? error?.message ?? '파일 업로드에 실패했습니다.',
        icon: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  // 모달에서 "정상 행만 저장" 실행 (strict=0)
  const handleProceedPartial = async () => {
    if (!selectedFile) return
    const formData = new FormData()
    formData.append('file', selectedFile)

    try {
      setLoading(true)
      const partialUrl = appendParam(uploadUrl, 'strict', '0')
      const res = await axiosWithAuth().post<UploadFinalResponse>(partialUrl, formData, {
        headers: {'Content-Type': 'multipart/form-data'},
      })

      setModalOpen(false)
      setValidation(null)
      setSelectedFile(null)

      if (isBefore) {
        await swal({
          title: '부분 저장 완료',
          text: '전월 자료 중 정상 행만 저장되었습니다.',
          icon: 'success',
          timer: 2600,
        })
      } else {
        await swal({
          title: '부분 저장 완료',
          text: `총 ${res.data.totalRows ?? 0}건 / 유효 ${res.data.validRows ?? 0}건`,
          icon: 'success',
          timer: 3000,
        })
      }

      onSuccess?.()
    } catch (error: any) {
      console.error('부분 저장 실패:', error)
      await swal({
        title: '실패',
        text: error?.response?.data?.message ?? error?.message ?? '부분 저장에 실패했습니다.',
        icon: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div className='flex gap-2 items-center'>
        <input
          type='text'
          readOnly
          value={selectedFile?.name || ''}
          placeholder='파일을 선택하세요'
          className='px-4 py-3 border w-[80%] border-gray-300 rounded-md text-lg bg-gray-50 cursor-pointer'
          onClick={() => !loading && document.getElementById('fileInput')?.click()}
        />
        <input
          id='fileInput'
          type='file'
          accept='.xlsx,.xls,.txt'
          className='hidden'
          disabled={loading}
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) setSelectedFile(file)
          }}
        />
        <button
          disabled={!selectedFile || disabled || loading}
          onClick={handleUpload}
          className={`px-4 py-3 text-lg text-white rounded-md transition-colors ${
            selectedFile && !disabled && !loading
              ? 'bg-indigo-500 hover:bg-indigo-600'
              : 'bg-gray-300 cursor-not-allowed'
          }`}
        >
          업로드
        </button>
      </div>

      {/* 업로드 중 오버레이 */}
      {loading && (
        <div className='fixed inset-0 bg-black bg-opacity-20 flex items-center justify-center z-50'>
          <div className='bg-white p-6 rounded-lg shadow-lg flex items-center gap-3'>
            <svg className='animate-spin h-6 w-6' viewBox='0 0 24 24' fill='none'>
              <circle
                className='opacity-25'
                cx='12'
                cy='12'
                r='10'
                stroke='currentColor'
                strokeWidth='4'
              />
              <circle
                className='opacity-75'
                cx='12'
                cy='12'
                r='10'
                stroke='currentColor'
                strokeWidth='4'
                strokeDasharray='80'
                strokeDashoffset='60'
                strokeLinecap='round'
              />
            </svg>
            <span className='text-lg font-semibold text-gray-700'>파일 업로드 중입니다...</span>
          </div>
        </div>
      )}

      {/* 검증 결과 모달 */}
      {validation && (
        <UploadValidationModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false)
            setValidation(null)
          }}
          data={validation}
          onProceedPartial={handleProceedPartial}
        />
      )}
    </>
  )
}

export default FileUpload
